import { Badge } from '@repletoryreach/sdk-ui';
import { formatNumber } from '@repletoryreach/sdk-utils/format';
import type { broadcasts, billing } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../i18n/I18nProvider';

/**
 * How a broadcast's delivery is read and worded, shared by the list, the broadcast page, its tabs and the
 * delivery report (4 October 2026).
 *
 * Delivery and read reports keep arriving for hours after the last message goes out (a phone that is off reports
 * when it comes back on), so a finished broadcast is not a settled one. The audit found the page stopped asking at
 * `finished_at` and the recipients list never asked at all.
 */

/** Every recipient status the service writes, in the order a message moves through them. */
export const RECIPIENT_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed', 'filtered_opted_out', 'cancelled'] as const;

/** How long after it finishes a broadcast is still worth asking about: reports rarely come later than this. */
export const REPORT_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Polling while it sends, and while reports are still coming in. */
export const RUNNING_POLL_MS = 5_000;
export const REPORT_POLL_MS = 30_000;

/** Within the window after it finished. */
export function finishedRecently(b: Pick<broadcasts.Broadcast, 'finished_at'>): boolean {
  if (!b.finished_at) return false;
  return Date.now() - new Date(b.finished_at).getTime() < REPORT_WINDOW_MS;
}

/**
 * How often to ask again about one broadcast, or false to stop. `awaiting` is the report's `awaiting_report`
 * (sent with no delivery report yet), when it is known.
 */
export function pollInterval(b: broadcasts.Broadcast | null | undefined, awaiting?: number | null): number | false {
  if (!b) return false;
  if (b.status_code === 'running') return RUNNING_POLL_MS;
  // A scheduled one that is due either starts or fails for want of credit within moments.
  if (b.status_code === 'scheduled' && b.scheduled_at && new Date(b.scheduled_at).getTime() <= Date.now() + 60_000) return REPORT_POLL_MS;
  if (finishedRecently(b) && (awaiting ?? awaitingOf(b)) > 0) return REPORT_POLL_MS;
  return false;
}

/**
 * Sent with no report yet, from the list's counters: `recipients_sent` counts everyone handed over and not failed
 * (delivered and read included), `recipients_delivered` everyone delivered (read included).
 */
export function awaitingOf(b: broadcasts.Broadcast): number {
  if (b.recipients_delivered === undefined) return 0;
  return Math.max(0, b.recipients_sent - b.recipients_delivered);
}

/** Everyone a message was handed over for: the base the rates are over, as the report counts it. */
export function attemptedOf(b: broadcasts.Broadcast): number {
  return b.recipients_sent + b.recipients_failed;
}

/** A rate as a whole-ish percentage, or null when there is nothing to divide by. */
export function rate(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part * 1000) / whole) / 10 : null;
}

/** "66,7%" in the reader's locale; empty when there is no rate. */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
}

/**
 * How far through its recipients a broadcast is: done is everything no longer queued (sent, delivered, read,
 * failed, left out or cancelled), so a broadcast whose last people failed still reaches 100%. From the report when
 * there is one (it counts queued exactly); from the row's counters otherwise, where a finished broadcast is done.
 */
export function progressOf(
  b: broadcasts.Broadcast,
  report?: Pick<broadcasts.BroadcastReport, 'total' | 'queued'> | null
): { done: number; total: number; queued: number; pct: number; complete: boolean } {
  const total = report?.total ?? b.recipients_total;
  let queued: number;
  if (report) queued = report.queued;
  else if (b.finished_at) queued = 0;
  else queued = Math.max(0, total - (b.recipients_sent + b.recipients_failed + b.recipients_filtered));
  const done = Math.max(0, total - queued);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (b.finished_at ? 100 : 0);
  return { done, total, queued, pct, complete: queued === 0 && (total > 0 || !!b.finished_at) };
}

/**
 * A recipient's reason in words. A code the catalogue knows is translated; Meta's own numbered errors read as
 * "WhatsApp refused it (error 131026)"; anything else falls back to the sentence the service wrote, or to a generic
 * sentence. A raw code is never shown on its own.
 */
export function reasonText(t: Translate, code: string | null | undefined, example?: string | null, payer?: 'platform' | 'customer'): string {
  // On the business's own WhatsApp account a billing refusal is theirs to fix in Meta (5 October 2026).
  if (code === 'ACCOUNT_NOT_PAYABLE' && payer === 'customer') return t('broadcasts.recipientError.ACCOUNT_NOT_PAYABLE.customer');
  if (code) {
    const known = t(`broadcasts.recipientError.${code}`, '');
    if (known) return known;
    const meta = /^META_(\d+)$/.exec(code);
    if (meta) return t('broadcasts.recipientError.META', { code: meta[1] });
  }
  if (example && example.trim()) return example.trim();
  return t('broadcasts.recipientError.unknown');
}

/** A recipient status in words. Read is WhatsApp's blue ticks. */
export function recipientStatusLabel(t: Translate, code: string | null | undefined): string {
  const c = code ?? 'queued';
  return t(`broadcasts.recipient.${c}`, t('broadcasts.recipient.unknown'));
}

/** Delivered and read are good news, sent is neutral, failed is red, left out is grey. */
export function recipientStatusColour(code: string | null | undefined): { color: string; variant: 'light' | 'outline' } {
  switch (code) {
    case 'read': return { color: 'green', variant: 'light' };
    case 'delivered': return { color: 'teal', variant: 'light' };
    case 'sent': return { color: 'gray', variant: 'light' };
    case 'failed': return { color: 'red', variant: 'light' };
    case 'filtered_opted_out': return { color: 'gray', variant: 'outline' };
    case 'cancelled': return { color: 'orange', variant: 'light' };
    case 'queued': return { color: 'blue', variant: 'light' };
    default: return { color: 'gray', variant: 'outline' };
  }
}

export function RecipientStatusBadge({ code, t, size = 'xs' }: { code: string | null | undefined; t: Translate; size?: 'xs' | 'sm' }) {
  const { color, variant } = recipientStatusColour(code);
  return <Badge size={size} variant={variant} color={color}>{recipientStatusLabel(t, code)}</Badge>;
}

/** A broadcast status in words. */
export function broadcastStatusLabel(t: Translate, code: string): string {
  return t(`broadcasts.status.${code}`, t('broadcasts.status.unknown'));
}

/**
 * Why a broadcast failed as a whole, in words: a scheduled one the credit could not cover, or one nobody was
 * reached by. Null for one that did not fail.
 */
export function broadcastFailureText(t: Translate, b: Pick<broadcasts.Broadcast, 'status_code' | 'failure_code' | 'failure_message'>): string | null {
  if (b.failure_code) {
    const known = t(`broadcasts.failure.${b.failure_code}`, '');
    if (known) return known;
    return b.failure_message?.trim() || t('broadcasts.failure.generic');
  }
  if (b.status_code === 'failed') return t('broadcasts.failure.nobodyReached');
  return null;
}

/**
 * A broadcast's spend: what was charged less what was refunded. The refund line only appears when something was
 * given back, so a clean send reads as one figure.
 */
export function spendLines(
  t: Translate,
  credits: (minor: number, currency?: string) => string,
  s: billing.UsageSource | null | undefined,
  fallbackCurrency?: string | null
): { net: string; detail: string | null } {
  if (!s) return { net: credits(0, fallbackCurrency ?? undefined), detail: null };
  const refunded = s.refunded_minor ?? 0;
  const charged = s.charged_minor ?? s.amount_minor + refunded;
  return {
    net: credits(s.amount_minor, s.currency),
    detail: refunded > 0
      ? t('broadcasts.spend.chargedRefunded', { charged: credits(charged, s.currency), refunded: credits(refunded, s.currency) })
      : null
  };
}
